/**
 * created 14.10.2025
 */
import React, {useEffect, useMemo, useRef, useState} from 'react';
import type {PropsWithChildren} from 'react';
import { ScreenshotContainer } from './ScreenshotContainer';
import './CourseSlider.css';

// A responsive, center-weighted screenshots slider with dot navigation and arrows.
// - Uses ScreenshotContainer for each image block
// - Smoothly scales items based on distance from the horizontal center
// - Supports clicking dots and left/right arrows to navigate

type CourseScreenshotsSliderComponentType = PropsWithChildren<{
  images: string[];
  title?: string;
  initialIndex?: number;
  className?: string;
  style?: React.CSSProperties;
  arrowButtons?: boolean;
}>;

export function CourseScreenshotsSlider({
                                          images,
                                          initialIndex = 0,
                                          className,
                                          style,
  arrowButtons = false,
  title = '',
}: CourseScreenshotsSliderComponentType) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<HTMLDivElement[]>([]);
  const [active, setActive] = useState(() => Math.max(0, Math.min(initialIndex, images.length - 1)));

  // Ensure refs array length matches images length
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, images.length);
  }, [images.length]);

  // Helper: center a given index in the scroll viewport
  const scrollToIndex = (index: number, behavior: ScrollBehavior = 'smooth') => {
    const c = containerRef.current;
    const el = itemRefs.current[index];
    if (!c || !el) return;
    const containerRect = c.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const currentScrollLeft = c.scrollLeft;
    const targetLeft = currentScrollLeft + (elRect.left - containerRect.left) - (containerRect.width - elRect.width) / 2;
    c.scrollTo({ left: targetLeft, behavior });
  };

  // On mount, center initial index
  useEffect(() => {
    const id = window.setTimeout(() => scrollToIndex(active, 'auto'), 0);
    return () => window.clearTimeout(id);
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  // Live scale update based on distance to center
  const updateScales = () => {
    const c = containerRef.current;
    if (!c) return;
    const cRect = c.getBoundingClientRect();
    const cCenter = cRect.left + cRect.width / 2;
    let bestIndex = 0;
    let bestDist = Number.POSITIVE_INFINITY;

    const minScale = 0.78;
    const maxScale = 1;

    itemRefs.current.forEach((el, i) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      const elCenter = r.left + r.width / 2;
      const dx = Math.abs(elCenter - cCenter);
      if (dx < bestDist) { bestDist = dx; bestIndex = i; }
      // Normalize distance: 0 at center, 1 around one container width away
      const norm = Math.min(1, dx / (cRect.width * 0.9));
      const scale = maxScale - (maxScale - minScale) * norm;
      const z = Math.round(1000 - norm * 1000);
      const opacity = 0.5 + (1 - norm) * 0.5; // 0.5..1
      el.style.transform = `scale(${scale})`;
      el.style.opacity = `${opacity}`;
      el.style.zIndex = `${z}`;
    });

    setActive((prev) => (prev !== bestIndex ? bestIndex : prev));
  };

  // Attach scroll handler with rAF throttling
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateScales);
    };
    c.addEventListener('scroll', onScroll, { passive: true });
    updateScales();
    const ro = new ResizeObserver(() => updateScales());
    ro.observe(c);
    return () => {
      c.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [images.length]);

  const goPrev = () => {
    const next = (active - 1 + images.length) % images.length;
    scrollToIndex(next);
  };
  const goNext = () => {
    const next = (active + 1) % images.length;
    scrollToIndex(next);
  };

  // Basic swipe support
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    let startX = 0;
    let isDown = false;
    const onPointerDown = (e: PointerEvent) => { isDown = true; startX = e.clientX; };
    const onPointerUp = (e: PointerEvent) => {
      if (!isDown) return;
      isDown = false;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 30) { dx > 0 ? goPrev() : goNext(); }
    };
    c.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      c.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [active, images.length]);//eslint-disable-line react-hooks/exhaustive-deps

  const dots = useMemo(() => new Array(images.length).fill(0), [images.length]);

  return (
    <div className={`${className ? className + ' ' : ''}course-slider-root`} style={{ ...style }}>
      {title && (<h2 className="course-slider-title">{title}</h2>)}
      {/* Title can be provided by parent; keep top space consistent with design screenshot via margin-bottom */}
      <div
        ref={containerRef}
        className="course-screenshots-slider"
      >
        <div
          key={'first'}
          className="course-slider-item course-slider-item-side"
        />
        {images.map((src, i) => (
          <div
            key={i}
            ref={(el) => { if (el) itemRefs.current[i] = el; }}
            className="course-slider-item"
          >
            <ScreenshotContainer
              screenshotUrl={src}
            />
          </div>
        ))}
        <div
          key={'last'}
          className="course-slider-item-side course-slider-item"
        />
      </div>

      {/* Arrows */}
      {arrowButtons && (
        <>
          <button
            type="button"
            aria-label="Previous"
            onClick={goPrev}
            className="course-slider-arrow course-slider-arrow--prev"
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Next"
            onClick={goNext}
            className="course-slider-arrow course-slider-arrow--next"
          >
            ›
          </button>
        </>
      )}


      {/* Dots */}
      <div className="course-slider-dots">
        {dots.map((_, i) => (
          <button
            key={i}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => scrollToIndex(i)}
            className={`course-slider-dot ${i === active ? 'is-active' : ''}`}
          />
        ))}
      </div>
    </div>
  );
}
